import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArchiveIcon from '@mui/icons-material/Archive';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Fab,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip
} from '@mui/material';
import useToDo from 'hooks/useToDo';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import SkeletonEarningCard from 'ui-component/cards/Skeleton/EarningCard';
import NoResults from 'views/components/common/NoResults';
import ExpandRow from './todo/ExpandRow';
import ToDoAdd from './todo/ToDoAdd';

const PersonalNotes = () => {
  const [openAddToDoList, setOpenAddToDoList] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selected, setSelected] = useState({ date: 'Last 7 Days' });
  const [filtersChanged, setFiltersChanged] = useState(false);
  const statusOrder = ['overdue', 'dueSoon'];
  const priorityOrder = ['high', 'medium', 'low'];
  const [ongoingPage, setOngoingPage] = useState(0);
  const [ongoingRowsPerPage, setOngoingRowsPerPage] = useState(5);
  const [archivePage, setArchivePage] = useState(0);
  const [archiveRowsPerPage, setArchiveRowsPerPage] = useState(5);

  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { todo, isLoading, updateToDo, deleteToDo, refetch } = useToDo(selected);

  const handleChangeState = async (event) => {
    let payload = {
      isCompleted: event.target.checked
    };
    let abb = event.target.name;
    await updateToDo({ id: abb, payload: payload });
    if (tabValue === 0) {
      setOngoingPage(0);
    } else {
      setArchivePage(0);
    }
    enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Todo marked as completed' }), { variant: 'success' });
  };
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
    // Reset the page to 0 when switching tabs
    if (newValue === 0) {
      setOngoingPage(0);
    } else {
      setArchivePage(0);
    }
  };

  const getStatusLabel = (todo) => {
    if (todo?.isCompleted) {
      return 'completed';
    }
    const today = moment().startOf('day');
    const dueDate = moment(todo?.dueDate).startOf('day');

    if (dueDate.isBefore(today)) {
      return 'overdue';
    } else if (dueDate.isSame(today) || dueDate.isAfter(today)) {
      return 'dueSoon';
    }
    return '';
  };

  const filteredTodos = todo?.filter((item) => {
    const isArchive = tabValue === 1;
    const statusMatch = statusFilter === '' || statusFilter === 'all' ? true : getStatusLabel(item) === statusFilter;
    const priorityMatch =
      priorityFilter === '' || priorityFilter === 'all' ? true : item.priority?.toLowerCase() === priorityFilter?.toLowerCase();

    if (isArchive) {
      return item?.isCompleted && statusMatch && priorityMatch;
    } else {
      return !item?.isCompleted && statusMatch && priorityMatch;
    }
  });

  const sortedTodos = filteredTodos?.sort((a, b) => {
    const statusA = getStatusLabel(a);
    const statusB = getStatusLabel(b);

    const priorityA = a.priority?.toLowerCase();
    const priorityB = b.priority?.toLowerCase();

    if (statusFilter === 'all' && priorityFilter === 'all') {
      // Sort by status first, then by priority
      if (statusA !== statusB) {
        return statusOrder.indexOf(statusA) - statusOrder.indexOf(statusB);
      } else {
        return priorityOrder.indexOf(priorityA) - priorityOrder.indexOf(priorityB);
      }
    }

    return 0;
  });

  const paginatedTodos = sortedTodos?.slice(
    tabValue === 0 ? ongoingPage * ongoingRowsPerPage : archivePage * archiveRowsPerPage,
    tabValue === 0 ? ongoingPage * ongoingRowsPerPage + ongoingRowsPerPage : archivePage * archiveRowsPerPage + archiveRowsPerPage
  );
  const handleChange = (event) => {
    setFiltersChanged(true);
    setSelected({ date: event.target.value });
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setOngoingPage(0);
    setArchivePage(0);
  }, [statusFilter, priorityFilter, selected]);

  const resetFilters = () => {
    setFiltersChanged(false);
    setSelected({ date: 'Last 7 Days' });
    setStatusFilter('all');
    setPriorityFilter('all');
  };

  return (
    <Card>
      {isLoading ? (
        <Box sx={{ height: 648, maxHeight: '100%' }}>
          <SkeletonEarningCard />
        </Box>
      ) : (
        <>
          <CardHeader
            action={
              <Box sx={{ height: 30 }}>
                {tabValue === 0 && (
                  <Stack direction="row" spacing={1}>
                    {filtersChanged && (
                      <Tooltip title={intl.formatMessage({ defaultMessage: 'Clear Filter' })}>
                        <IconButton size="small" color="secondary" onClick={resetFilters} aria-label="clear filters">
                          <FilterAltOffOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Stack direction="row" spacing={1}>
                      <TextField
                        select
                        label={intl.formatMessage({ defaultMessage: 'Range' })}
                        value={selected?.date}
                        onChange={handleChange}
                        variant="outlined"
                        size="small"
                        sx={{ width: '7rem', marginRight: 1 }}
                      >
                        <MenuItem value="Today">{intl.formatMessage({ defaultMessage: 'Today' })}</MenuItem>
                        <MenuItem value="Yesterday">{intl.formatMessage({ defaultMessage: 'Yesterday' })}</MenuItem>
                        <MenuItem value="This Week">{intl.formatMessage({ defaultMessage: 'This Week' })}</MenuItem>
                        <MenuItem value="Last 7 Days">{intl.formatMessage({ defaultMessage: 'Last 7 Days' })}</MenuItem>
                        <MenuItem value="Previous Week">{intl.formatMessage({ defaultMessage: 'Previous Week' })}</MenuItem>
                        <MenuItem value="This Month">{intl.formatMessage({ defaultMessage: 'This Month' })}</MenuItem>
                        <MenuItem value="Last 30 Days">{intl.formatMessage({ defaultMessage: 'Last 30 Days' })}</MenuItem>
                        <MenuItem value="Last Month">{intl.formatMessage({ defaultMessage: 'Last Month' })}</MenuItem>
                      </TextField>

                      <TextField
                        select
                        label={intl.formatMessage({ defaultMessage: 'Status' })}
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setFiltersChanged(true);
                        }}
                        variant="outlined"
                        size="small"
                        sx={{ width: '7rem', marginRight: 1 }}
                      >
                        <MenuItem value="all">{intl.formatMessage({ defaultMessage: 'All' })}</MenuItem>
                        <MenuItem value="overdue">{intl.formatMessage({ defaultMessage: 'Overdue' })}</MenuItem>
                        <MenuItem value="dueSoon">{intl.formatMessage({ defaultMessage: 'Due Soon' })}</MenuItem>
                      </TextField>

                      <TextField
                        select
                        label={intl.formatMessage({ defaultMessage: 'Priority' })}
                        value={priorityFilter}
                        onChange={(e) => {
                          setPriorityFilter(e.target.value);
                          setFiltersChanged(true);
                        }}
                        variant="outlined"
                        size="small"
                        sx={{ width: '7rem' }}
                      >
                        <MenuItem value="all">{intl.formatMessage({ defaultMessage: 'All' })}</MenuItem>
                        <MenuItem value="low">{intl.formatMessage({ defaultMessage: 'Low' })}</MenuItem>
                        <MenuItem value="medium">{intl.formatMessage({ defaultMessage: 'Medium' })}</MenuItem>
                        <MenuItem value="high">{intl.formatMessage({ defaultMessage: 'High' })}</MenuItem>
                      </TextField>

                      <Fab size="small" color="primary" aria-label="new todo add" onClick={() => setOpenAddToDoList(true)}>
                        <AddRoundedIcon fontSize="small" />
                      </Fab>
                    </Stack>
                  </Stack>
                )}
              </Box>
            }
            title={
              <Tooltip title={intl.formatMessage({ defaultMessage: 'To Do List' })}>
                <Box
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '150px',
                    '&:hover': {
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  {intl.formatMessage({ defaultMessage: 'To Do List' })}
                </Box>
              </Tooltip>
            }
          />
          <Divider />

          <Tabs sx={{ height: 20, alignItems: 'center' }} value={tabValue} onChange={handleChangeTab} aria-label="todo list tabs">
            <Tab
              icon={<ListAltIcon style={{ fontSize: '1rem' }} />}
              iconPosition="start"
              label={intl.formatMessage({ defaultMessage: 'Ongoing' })}
            />
            <Tab
              icon={<ArchiveIcon style={{ fontSize: '1rem' }} />}
              iconPosition="start"
              label={intl.formatMessage({ defaultMessage: 'Archive' })}
            />
          </Tabs>

          <CardContent>
            {!filteredTodos?.length ? (
              <Box sx={{ height: 480 }}>
                <NoResults>
                  <FormattedMessage defaultMessage="No Tasks Found" />
                </NoResults>
              </Box>
            ) : (
              <>
                <Box sx={{ height: 480, maxHeight: '100%' }}>
                  <TableContainer
                    sx={{
                      height: 400,
                      maxHeight: '100%',
                      overflowX: 'scroll',
                      '&::-webkit-scrollbar': {
                        height: 10,
                        width: 4
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#ddd',
                        borderRadius: 3
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#555'
                      }
                    }}
                  >
                    <Table aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell sx={{ padding: 1 }}>{intl.formatMessage({ defaultMessage: 'Title' })}</TableCell>
                          <TableCell sx={{ padding: 1 }}>{intl.formatMessage({ defaultMessage: 'Priority' })}</TableCell>
                          <TableCell sx={{ padding: 1 }}>{intl.formatMessage({ defaultMessage: 'Created On' })}</TableCell>
                          <TableCell sx={{ padding: 1 }}>{intl.formatMessage({ defaultMessage: 'Status' })}</TableCell>
                          <TableCell sx={{ padding: 1 }}>{intl.formatMessage({ defaultMessage: 'Action' })}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedTodos.map((data, index) => (
                          <ExpandRow todo={data} handleChangeState={handleChangeState} key={index} selectedDate={selected} />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[]}
                    component="div"
                    count={filteredTodos.length}
                    rowsPerPage={tabValue === 0 ? ongoingRowsPerPage : archiveRowsPerPage}
                    page={tabValue === 0 ? ongoingPage : archivePage}
                    onPageChange={(e, newPage) => {
                      if (tabValue === 0) {
                        setOngoingPage(newPage);
                      } else {
                        setArchivePage(newPage);
                      }
                    }}
                    onRowsPerPageChange={(event) => {
                      const newRowsPerPage = parseInt(event.target.value, 10);
                      if (tabValue === 0) {
                        setOngoingRowsPerPage(newRowsPerPage);
                        setOngoingPage(0);
                      } else {
                        setArchiveRowsPerPage(newRowsPerPage);
                        setArchivePage(0);
                      }
                    }}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </>
      )}
      {openAddToDoList && <ToDoAdd handleCloseDialog={() => setOpenAddToDoList(false)} selectedDate={selected} />}
    </Card>
  );
};

export default PersonalNotes;
